import { useEffect, useState } from "react";
import api from "../api";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { loadRun, TOTAL_STEPS } from "../lib/runState";

interface AppShellProps {
  username: string;
  onLogout: () => void;
}

const navItems = [
  { to: "/", label: "Home", icon: "🏠", end: true },
  { to: "/workflows", label: "Workflows", icon: "🔁", end: false },
  { to: "/tools", label: "Tools", icon: "🧰", end: false },
  { to: "/bots", label: "Automation Bots", icon: "🤖", end: false },
  { to: "/roadmap", label: "Roadmap", icon: "🗺️", end: false },
];

/** Whether the signed-in person administers Pioneer. Asked once; the server
 *  enforces it regardless, so a failed answer just hides the link. */
function useIsAdministrator() {
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    api.get("/my-access").then((r) => setAdmin(!!r.data?.administrator)).catch(() => setAdmin(false));
  }, []);
  return admin;
}

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const isAdmin = useIsAdministrator();
  const run = loadRun();
  const runActive = run.step <= TOTAL_STEPS;
  return (
    <>
      <div className="eyebrow px-3 pt-4 pb-1">Operations</div>
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-sm2 font-semibold transition ${
              isActive || (item.to === "/tools" && location.pathname.startsWith("/tools"))
                ? "bg-brand-tint text-brand-deep"
                : "text-ink-soft hover:bg-paper-2"
            }`
          }
        >
          <span>{item.icon}</span> {item.label}
          {item.to === "/workflows" && runActive && (
            <span className="ml-auto pill bg-wnv-tint text-wnv">1</span>
          )}
        </NavLink>
      ))}
      {isAdmin && (
        <>
          <div className="eyebrow px-3 pt-4 pb-1">Administration</div>
          <NavLink
            to="/admin"
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm2 font-semibold transition ${
                isActive ? "bg-brand-tint text-brand-deep" : "text-ink-soft hover:bg-paper-2"
              }`
            }
          >
            <span>🔐</span> People &amp; access
          </NavLink>
        </>
      )}
      <div className="eyebrow px-3 pt-4 pb-1">Shortcuts</div>
      <NavLink
        to="/workflows/adult-control"
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-sm2 font-semibold transition ${
            isActive ? "bg-wnv-tint text-wnv" : "text-ink-soft hover:bg-paper-2"
          }`
        }
      >
        <span>🚨</span> Adult Control Run
      </NavLink>
    </>
  );
}

export default function AppShell({ username, onLogout }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const screenTitle = location.pathname.startsWith("/workflows")
    ? "Workflows"
    : location.pathname.startsWith("/tools")
    ? "Tools"
    : location.pathname.startsWith("/roadmap")
    ? "Roadmap"
    : location.pathname.startsWith("/admin")
    ? "People & access"
    : "Home";

  const footer = (
    <div className="mt-auto border-t border-line pt-3 px-3 pb-3">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-brand-tint text-brand-deep flex items-center justify-center font-bold text-sm">
          {username.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{username}</div>
          <div className="text-xs text-ink-faint flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-brand inline-block" /> AGOL connected
          </div>
        </div>
      </div>
      <button onClick={onLogout} className="btn-ghost w-full mt-3 !py-1.5 text-xs">Log out</button>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden min-[880px]:flex flex-col w-[248px] shrink-0 bg-card border-r border-line px-3 py-4 sticky top-0 h-screen">
        {/* The mark is the way back to the home page, which is where people
            already try to click. Rendered as a Link so it behaves like one --
            keyboard focus, middle-click to a new tab -- rather than a div
            with an onClick that only answers the mouse. */}
        <Link
          to="/"
          className="flex items-center gap-2.5 px-3 pb-3 border-b border-line hover:opacity-80 transition-opacity"
          aria-label="Pioneer home"
        >
          <img src="/logo512.png" alt="CMAD" className="w-9 h-9" />
          <div>
            <div className="font-extrabold text-[15px] leading-tight">Pioneer</div>
            <div className="eyebrow !pt-0">Consolidated MAD</div>
          </div>
        </Link>
        <nav className="flex flex-col gap-0.5">
          <NavLinks />
        </nav>
        {footer}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 min-[880px]:hidden" onClick={() => setDrawerOpen(false)}>
          <div className="absolute inset-0 bg-ink/40" />
          <aside className="absolute left-0 top-0 bottom-0 w-[270px] bg-card shadow-lg2 px-3 py-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <nav className="flex flex-col gap-0.5">
              <NavLinks onNavigate={() => setDrawerOpen(false)} />
            </nav>
            {footer}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-paper/90 backdrop-blur border-b border-line px-5 py-3 flex items-center gap-3">
          <button className="min-[880px]:hidden btn-ghost !px-2.5 !py-1" onClick={() => setDrawerOpen(true)}>☰</button>
          <h1 className="font-extrabold text-lg">{screenTitle}</h1>
          <span className="ml-auto pill bg-brand-tint text-brand-deep">AGOL synced</span>
        </header>

        <main className="flex-1 w-full max-w-content mx-auto px-4 min-[880px]:px-[26px] py-6 pb-[92px] min-[880px]:pb-8">
          <Outlet />
        </main>

        {/* Bottom tab bar (mobile) */}
        <nav className="min-[880px]:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-line flex justify-around py-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-4 py-1 text-xs font-semibold rounded-sm2 min-w-[64px] ${
                  isActive ? "text-brand-deep" : "text-ink-faint"
                }`
              }
            >
              <span className="text-lg">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
